import './BatchTable.css';

import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Table, TableCell, TableHeading, Spinner, Card, Icon } from '@nike/eds';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { LocalClearanceBatch } from '../../shared/Types';
import { StoreDispatch } from '../../shared/store/Store';
import { toggleBatch, selectIsLoading, selectBatches } from '../../shared/store/reducers/OverviewReducer';
import { ExceptionsCounter } from '../exceptions_counter/ExceptionsCounter';
import { ProgressIndicator } from '../progress_indicator/ProgressIndicator';
import { AdvancedProgressIndicator, ProgressSegment } from '../advanced_progress_indicator/AdvancedProgressIndicator';
import DateTimeFormat from "../shared/DateTimeFormat";

export function BatchTable() {
    const dispatch: StoreDispatch = useDispatch();
    const data = useSelector(selectBatches) as LocalClearanceBatch[];
    const isLoading = useSelector(selectIsLoading) as boolean;

    const onChange = (batch: LocalClearanceBatch) => {
        dispatch(toggleBatch(batch));
    }

    const goToExceptionsDashboard = (date: string) => {
        window.history.pushState(null, '', `/local-clearance/exceptions?momentOfLocalClearance=${date}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
    }

    const columnHelper = createColumnHelper<LocalClearanceBatch>();
    const columns = [
        columnHelper.display({
            id: 'select_batch',
            header: () => '',
            cell: (props) =>  {
                const batch = props.row.original;
                return batch.selectable ? <Checkbox id={`batch-checkbox-${props.row.id}`} label="" value={batch.day_bucket_date} checked={batch.selected} onChange={e => onChange(batch)}/> : null;
            },
            minSize: 0,
            maxSize: 25,
        }),
        columnHelper.accessor("day_bucket_date", {
            header: "EiDR batch",
            id: "eidr_batch",
            minSize: 0,
            maxSize: 100,
        }),
        columnHelper.accessor("totalEntries", {
            header: "# EiDRs",
            id: "total_entries",
            minSize: 0,
            maxSize: 80,
        }),
        columnHelper.display({
            header: "EiDRs status",
            id: "eidrs_status",
            cell: props => {
                const batch = props.row.original;
                const segments: ProgressSegment[] = [
                    { label: 'Released', count: batch.releasedEntries || 0, color: '#4caf50' },
                    { label: 'Enriched', count: batch.enrichedEntries || 0, color: '#9e9e9e' },
                    { label: 'Exception', count: batch.exceptionCount || 0, color: '#f44336' },
                    { label: 'Processing', count: batch.enrichingEntries || 0, color: '#e0e0e0' },
                ];
                return <AdvancedProgressIndicator
                    id={`eidrs-status-${props.row.id}`}
                    segments={segments}
                    total={batch.totalEntries || 0}
                />;
            },
            minSize: 0,
            maxSize: 140,
        }),
        columnHelper.display({
            header: "Exceptions",
            id: "exceptions",
            cell: props => {
                const exceptionCount = props.row.original.exceptionCount;
                if(exceptionCount && exceptionCount > 0) {
                    return (
                        <div className="exceptions-container">
                            <Icon className="exceptions-icon" name="InfoCircle" color="red" onClick={() => { goToExceptionsDashboard(props.row.original.day_bucket_date)}}/>
                            <ExceptionsCounter exceptionsLength={exceptionCount} />
                        </div>
                    );
                }
                return <></>;
            },
            minSize: 0,
            maxSize: 70,
        }),
        columnHelper.accessor("released_by", {
            header: "Released by",
            id: "released_by",
            cell: props => (props.getValue() || '').replace(/@nike\.com$/i, '').replace(/\./g, ' '),
            minSize: 0,
            maxSize: 120,
        }),
        columnHelper.accessor("released_at", {
            header: "Released at",
            id: "released_at",
            cell: props => <DateTimeFormat timestamp={props.getValue()} format="dd/MM/yyyy, HH:mm" />,
            minSize: 0,
            maxSize: 120,
        }),
        columnHelper.accessor("total_products", {
            header: "# Products",
            id: "total_products",
            minSize: 0,
            maxSize: 80,
        }),
        columnHelper.display({
            header: "Products ready for FP",
            id: "products_ready_for_fp",
            cell: props => {
                const batch = props.row.original;
                return <ProgressIndicator
                    id={`gagis-progress-${props.row.id}`}
                    total={batch.total_products || 0}
                    completed={batch.products_in_gagis || 0}
                />;
            },
            minSize: 0,
            maxSize: 140,
        }),
        columnHelper.display({
            header: "Products in FP",
            id: "products_in_fp",
            cell: props => {
                const batch = props.row.original;
                return <ProgressIndicator
                    id={`fp-progress-${props.row.id}`}
                    total={batch.total_products || 0}
                    completed={batch.products_in_filing_pack || 0}
                />;
            },
            minSize: 0,
            maxSize: 140,
        })
    ];

    const {getHeaderGroups, getRowModel} = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <div id="batch-table-wrapper">
            <div id="loader" className={!isLoading ? 'hidden' : ''}>
                <Spinner size="large" />
            </div>
            <Card id="message-no-batches" className={isLoading || data.length > 0 ? 'hidden' : ''}>
                <p className="'text-center'">No batches available for selected month, because it is the first of the month.</p>
            </Card>
            <div id="batch-table-container" className={isLoading || data.length === 0 ? 'hidden' : ''}>
            <Table id="batch-table" style={{overflow: 'visible'}}>
                <thead>
                {getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, index) => (
                                <TableHeading
                                    key={header.id}
                                    style={{
                                        minWidth: header.column.columnDef.minSize,
                                        maxWidth: header.column.columnDef.maxSize
                                    }}
                                    className={`eds-type--subtitle-1 eds-color--default ${index === 0 ? "table-action-header" : "table-header"} ${header.column.id === "total_products" ? "section-divider" : ""}`}
                                >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHeading>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map((cell, index) => (
                            <TableCell
                                key={cell.id}
                                className={`eds-type--body-3 ${index === 0 ? "table-action-data" : "table-data"} ${cell.column.id === "total_products" ? "section-divider" : ""}`}
                                style={{
                                    minWidth: cell.column.columnDef.minSize,
                                    maxWidth: cell.column.columnDef.maxSize
                                }}
                            >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                        ))}
                    </tr>
                ))}
                </tbody>
            </Table>
            </div>
        </div>
    )
}